import { useEffect, useState } from "react";
import styles from "./AdminCupons.module.css";
import Header from "../../components/Header/Header";
import AdminSideBar from "../../components/SideBar/SideBar";
import Footer from "../../components/footer/Footer";
import Seta from "../../assets/SetaDireita.svg";
import { useTranslation } from "react-i18next";
import api from "../../utils/axios/api";

interface Coupon {
    id: string;
    name: string;
    discount: number;
    quantity?: number | null;
    validDate: string;
}

const emptyForm = {
    name: "",
    discount: "",
    quantity: "",
    validDate: "",
};

function useMediaQuery(query: string): boolean {
    const [matches, setMatches] = useState(false);
    useEffect(() => {
        const media = window.matchMedia(query);
        setMatches(media.matches);
        const listener = () => setMatches(media.matches);
        media.addEventListener("change", listener);
        return () => media.removeEventListener("change", listener);
    }, [query]);
    return matches;
}

const isVoucherActive = (validDate: string): boolean => {
    return new Date(validDate) >= new Date();
};

export default function AdminCupons() {
    const { t } = useTranslation("translation", { keyPrefix: "adminCupons" });
    const isSmallScreen = useMediaQuery("(max-width: 768px)");
    const [mobileMenuActive, setMobileMenuActive] = useState(false);

    const [coupons, setCoupons] = useState<Coupon[]>([]);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);

    const fetchCoupons = async () => {
        try {
            const response = await api.get("/admin/vouchers");
            setCoupons(response.data);
        } catch (error) {
            console.error("Erro ao buscar cupons:", error);
        }
    };

    useEffect(() => {
        fetchCoupons();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        if (type === "checkbox") {
            setForm(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
        } else {
            setForm(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleSubmit = async () => {
        if (!form.name || !form.discount || !form.validDate) {
            alert(t("requiredFields"));
            return;
        }

        try {
            if (editingId) {
                const payload: any = {
                    name: form.name,
                    value: parseFloat(form.discount),
                };
                await api.put(`/admin/vouchers/${editingId}`, payload);
                alert(t("updatedSuccess"));
            } else {
                const payload: any = {
                    name: form.name,
                    discount: parseFloat(form.discount),
                    quantity: form.quantity ? parseInt(form.quantity) : undefined,
                    validDate: new Date(form.validDate).toISOString(),
                };
                await api.post("/admin/vouchers", payload);
                alert(t("createdSuccess"));
            }
            setForm(emptyForm);
            setEditingId(null);
            setShowForm(false);
            fetchCoupons();
        } catch (error) {
            console.error("Erro ao salvar cupom:", error);
            alert(t("saveError"));
        }
    };

    const handleEdit = (coupon: Coupon) => {
        setForm({
            name: coupon.name,
            discount: String(coupon.discount),
            quantity: coupon.quantity != null ? String(coupon.quantity) : "",
            validDate: coupon.validDate.slice(0, 10),
        });
        setEditingId(coupon.id);
        setShowForm(true);
        globalThis.scrollTo(0, 0);
    };

    const handleDelete = async (id: string) => {
        if (!confirm(t("confirmDelete"))) return;
        try {
            await api.delete(`/admin/vouchers/${id}`);
            fetchCoupons();
        } catch (error) {
            console.error("Erro ao deletar cupom:", error);
            alert(t("deleteError"));
        }
    };

    const handleCancel = () => {
        setForm(emptyForm);
        setEditingId(null);
        setShowForm(false);
    };

    return (
        <>
            <Header
                boxShadow
                toggleMobileMenu={setMobileMenuActive}
                mobileMenuActiveState={mobileMenuActive}
                isSmallScreen={isSmallScreen}
            />
            <main className={styles.mainContainer}>
                <section className={styles.sectionContainer}>
                    <div className={styles.caminho}>
                        <span>{t("myAccount")}</span>
                        <img src={Seta} alt="seta" />
                        <span className={styles.local}>{t("coupons")}</span>
                    </div>

                    <div className={styles.tituloContainer}>
                        <p className={styles.titulo}>{t("coupons")}</p>
                        {!showForm && (
                            <button className={styles.buttonNew} onClick={() => setShowForm(true)}>
                                + {t("newCoupon")}
                            </button>
                        )}
                    </div>

                    {showForm && (
                        <div className={styles.formContainer}>
                            <h2 className={styles.formTitle}>
                                {editingId ? t("editCoupon") : t("newCoupon")}
                            </h2>

                            <div className={styles.formGrid}>
                                <div className={styles.formGroup}>
                                    <label>{t("code")}</label>
                                    <input
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        placeholder={t("codePlaceholder")}
                                        className={styles.input}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label>{t("discount")} (%)</label>
                                    <input
                                        name="discount"
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        value={form.discount}
                                        onChange={handleChange}
                                        placeholder="0,00"
                                        className={styles.input}
                                    />
                                </div>

                                {!editingId && (
                                    <>
                                        <div className={styles.formGroup}>
                                            <label>{t("quantity")} ({t("optional")})</label>
                                            <input
                                                name="quantity"
                                                type="number"
                                                min="1"
                                                value={form.quantity}
                                                onChange={handleChange}
                                                placeholder={t("unlimitedPlaceholder")}
                                                className={styles.input}
                                            />
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label>{t("validDate")}</label>
                                            <input
                                                name="validDate"
                                                type="date"
                                                value={form.validDate}
                                                onChange={handleChange}
                                                className={styles.input}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className={styles.formActions}>
                                <button className={styles.buttonSalvar} onClick={handleSubmit}>
                                    {t("save")}
                                </button>
                                <button className={styles.buttonCancelar} onClick={handleCancel}>
                                    {t("cancel")}
                                </button>
                            </div>
                        </div>
                    )}

                    <div className={styles.tableWrapper}>
                        {coupons.length === 0 ? (
                            <p className={styles.empty}>{t("noCoupons")}</p>
                        ) : (
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>{t("code")}</th>
                                        <th>{t("discount")}</th>
                                        <th>{t("quantity")}</th>
                                        <th>{t("validDate")}</th>
                                        <th>{t("status")}</th>
                                        <th>{t("actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {coupons.map(coupon => (
                                        <tr key={coupon.id}>
                                            <td><strong>{coupon.name}</strong></td>
                                            <td>{coupon.discount.toFixed(2).replace(".", ",")}%</td>
                                            <td>{coupon.quantity ?? t("unlimited")}</td>
                                            <td>{new Date(coupon.validDate).toLocaleDateString("pt-BR")}</td>
                                            <td>
                                                <span className={isVoucherActive(coupon.validDate) ? styles.badgeActive : styles.badgeInactive}>
                                                    {isVoucherActive(coupon.validDate) ? t("active") : t("inactive")}
                                                </span>
                                            </td>
                                            <td className={styles.actions}>
                                                <button className={styles.buttonEdit} onClick={() => handleEdit(coupon)}>
                                                    {t("edit")}
                                                </button>
                                                <button className={styles.buttonDelete} onClick={() => handleDelete(coupon.id)}>
                                                    {t("delete")}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </section>

                <aside className={styles.asideContainer}>
                    <AdminSideBar />
                </aside>
            </main>
            {!isSmallScreen && <Footer />}
        </>
    );
}